import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { z } from "zod";

import { axios } from "@/client/libs/axios";
import { useUser } from "@/client/services/user";

const coerceDateToISOString = z
  .union([z.string(), z.date()])
  .transform((v) => (v instanceof Date ? v.toISOString() : v));

const ambassadorResponseSchema = z.object({
  eligible: z.boolean(),

  state: z.enum(["purchase-required", "not-created", "available", "blocked"]),

  code: z.string().nullable(),

  earnedFcfa: z.number().int().nonnegative(),

  confirmedSales: z.number().int().nonnegative(),

  sales: z.array(
    z.object({
      paymentId: z.string(),
      codeSnapshot: z.string(),
      planCode: z.string(),
      amountToPayFcfa: z.number().int().nonnegative(),
      commissionFcfa: z.number().int().nonnegative(),
      createdAt: coerceDateToISOString,
      payment: z.object({
        status: z.number().int(),
      }),
      commission: z
        .object({
          amountFcfa: z.number().int().nonnegative(),
          createdAt: coerceDateToISOString,
        })
        .nullable(),
    }),
  ),

  pagination: z.object({
    page: z.number().int().positive(),
    pageSize: z.number().int().positive(),
    total: z.number().int().nonnegative(),
  }),
});

export type AmbassadorResponse = z.infer<typeof ambassadorResponseSchema>;

const getInitials = (value: string) => {
  return value
    .split(/[_\-\s]+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part.charAt(0).toUpperCase())
    .join("");
};

export const useAmbassadorDashboard = (page = 1) => {
  const { user } = useUser();
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: ["user-space", "ambassador", user?.id, page],

    enabled: Boolean(user?.id),

    queryFn: async ({ signal }) => {
      const response = await axios.get<unknown>("/engagement/ambassador", {
        params: { page },
        signal,
      });

      return ambassadorResponseSchema.parse(response.data);
    },

    staleTime: 10_000,
    refetchOnWindowFocus: true,
  });

  const createCodeMutation = useMutation({
    mutationFn: async () => {
      const response = await axios.post<unknown>("/engagement/ambassador/code");

      return z
        .object({
          state: z.string(),
          code: z.string().nullable(),
        })
        .parse(response.data);
    },

    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: ["user-space", "ambassador", user?.id],
      });
    },
  });

  const rawData = query.data;

  /**
   * Adaptation de la réponse backend vers les composants
   * visuels déjà présents dans la page.
   *
   * L'identité de l'acheteur n'est volontairement pas
   * exposée par le backend.
   */
  const usages =
    rawData?.sales.map((sale) => ({
      id: sale.paymentId,

      customerName: "Utilisateur monCVpro",

      customerInitials: getInitials(sale.codeSnapshot) || "MC",

      purchaseDate: sale.createdAt,

      offerName: sale.planCode.replace("SMART_", "SMART "),

      amountPaid: sale.amountToPayFcfa,

      status: sale.payment.status === 1 ? ("active" as const) : ("inactive" as const),
    })) ?? [];

  return {
    data: rawData,

    usages,

    loading: query.isPending,
    refreshing: query.isFetching,

    error: query.error,

    refetch: query.refetch,

    createCode: () => createCodeMutation.mutateAsync(),

    creatingCode: createCodeMutation.isPending,
  };
};
